import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import Grid from '@material-ui/core/Grid';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Typography from '@material-ui/core/Typography';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { useSetupStyles } from './style';

interface AccountStepProps {
  control: any;
  errors: any;
}

const sanitizePhoneNumber = (value: string): string =>
  value.replace(/[^\d]/g, '').slice(0, 10);

export default function AccountStep({ control, errors }: AccountStepProps) {
  const classes = useSetupStyles();
  const [showPassword, setShowPassword] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');

  return (
    <div className={classes.stepContent}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="body1" className={classes.formLabel}>
            First Name
          </Typography>
          <Controller
            className={classes.textField}
            variant="outlined"
            name="name"
            control={control}
            rules={{ required: 'First Name is required.' }}
            defaultValue=""
            as={
              <OutlinedInput
                placeholder="First Name"
                className={classes.inputStyle}
                labelWidth={0}
              />
            }
          />
          <ErrorMessage as={<Typography color="error" />} errors={errors} name="name" />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body1" className={classes.formLabel}>
            Last Name
          </Typography>
          <Controller
            className={classes.textField}
            variant="outlined"
            name="lastName"
            control={control}
            rules={{ required: 'Last Name is required.' }}
            defaultValue=""
            as={
              <OutlinedInput
                placeholder="Last Name"
                className={classes.inputStyle}
                labelWidth={0}
              />
            }
          />
          <ErrorMessage as={<Typography color="error" />} errors={errors} name="lastName" />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body1" className={classes.formLabel}>
            Email
          </Typography>
          <Controller
            className={classes.textField}
            variant="outlined"
            name="email"
            control={control}
            rules={{ required: 'Email is required.' }}
            defaultValue=""
            as={
              <OutlinedInput
                placeholder="Email"
                className={classes.inputStyle}
                labelWidth={0}
                type="email"
              />
            }
          />
          <ErrorMessage as={<Typography color="error" />} errors={errors} name="email" />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body1" className={classes.formLabel}>
            Password
          </Typography>
          <Controller
            className={classes.textField}
            variant="outlined"
            name="password"
            control={control}
            rules={{ required: 'Password is required.' }}
            defaultValue=""
            as={
              <OutlinedInput
                placeholder="Password"
                className={classes.inputStyle}
                type={showPassword ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      style={{ color: '#D3C3E6' }}
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
                labelWidth={0}
              />
            }
          />
          <ErrorMessage as={<Typography color="error" />} errors={errors} name="password" />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body1" className={classes.formLabel}>
            Phone Number (Optional)
          </Typography>
          <Controller
            className={classes.textField}
            variant="outlined"
            name="phoneNumber"
            control={control}
            rules={{
              validate: (value: string) => {
                if (!value || value.trim() === '') return true;
                const digits = (value || '').replace(/[^\d]/g, '');
                return digits.length === 10 || 'Please enter a valid 10-digit phone number';
              },
            }}
            defaultValue=""
            as={
              <OutlinedInput
                placeholder="5551234567"
                value={phoneNumber}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setPhoneNumber(sanitizePhoneNumber(e.target.value));
                }}
                inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                labelWidth={0}
                className={classes.inputStyle}
              />
            }
          />
          <ErrorMessage as={<Typography color="error" />} errors={errors} name="phoneNumber" />
        </Grid>
      </Grid>
    </div>
  );
}
